import React from 'react';

import throttle from 'lodash/throttle';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';

import type { Alignment, Item } from '../../../types';
import { useOrientationScrolling } from '../hooks/use-orientation-scrolling';
import { useWidthAndSpacingCalculations } from '../hooks/use-width-and-spacing-calculations';
import { CardOrientationWrapper } from '../item-orientation-wrapper';
import type { ItemDimensions } from '../types';

// Design wants to always have a 16px gap between cards in thumbnail navigation,
// Therefore, don't want to modify the calculations logic, so just updating here for now
export const MIN_GAP_BETWEEN_CARDS = 16;
export const MAX_GAP_BETWEEN_CARDS = 16;

type CardsOrientationProps<TItem> = {
	alignment: Alignment;
	cardDimensions: ItemDimensions;
	cards: TItem[];
	cardsContentFooter?: React.ComponentType<any>;
	extensionLocalId: string;
	isInViewMode: boolean;
	isStacked: boolean;
	numberOfCards: number;
	renderCard: (card: TItem) => React.ReactElement;
	showAIDisclaimer?: boolean;
};

const styles = cssMap({
	flexBox: {
		display: 'flex',
		position: 'relative',
		paddingLeft: token('space.0'),
	},
	stacked: {
		flexWrap: 'wrap',
	},
	row: {
		overflow: 'hidden',
	},
	left: {
		justifyContent: 'flex-start',
	},
	center: {
		justifyContent: 'center',
	},
	right: {
		justifyContent: 'flex-end',
	},
});

export const CardsOrientation = <TItem extends Item>({
	numberOfCards,
	cardDimensions,
	alignment,
	isStacked,
	isInViewMode,
	extensionLocalId,
	cards,
	renderCard,
	showAIDisclaimer,
	cardsContentFooter: CardsContentFooter,
}: CardsOrientationProps<TItem>) => {
	const { calculations, setCurrentCardsGridWidth, setCurrentCardWidth } =
		useWidthAndSpacingCalculations({
			numberOfCards,
			cardDimensions: cardDimensions,
		});

	const {
		cardWidthOnFullRow,
		isFirstRowFull,
		isGridSmallerOrEqualWidthToCards,
		gaps,
		maxNumberOfCardsPerRow,
	} = calculations;

	const throttledSetWidth = throttle(setCurrentCardsGridWidth, 200);
	const orientation = isStacked ? 'grid' : 'row';
	const spaceBetweenCards = gaps.spaceBetweenCards;

	const {
		isAtBeginning,
		isAtEnd,
		scrollToPrevious,
		scrollToNext,
		scrollToIndex,
		requestScrollIndex,
		resetScrollIndex,
	} = useOrientationScrolling({
		maxNumberOfItemsPerRow: maxNumberOfCardsPerRow,
		numberOfItems: cards?.length,
		isRow: !isStacked,
	});

	const allCardsShown = isInViewMode && isAtBeginning && isAtEnd;

	return (
		<Stack>
			<Box
				testId={`link-cards-content-cards-${orientation}`}
				xcss={cx(
					styles.flexBox,
					isStacked ? styles.stacked : styles.row,
					/**
					 * Align left regardless of alignment if
					 * - orientation is row
					 * - the grid width is smaller or equal to the minimum width of the card size
					 */
					!isStacked || isGridSmallerOrEqualWidthToCards ? styles.left : styles[alignment],
				)}
				style={{
					gap: `${spaceBetweenCards}px`,
				}}
			>
				{cards.map((card, index) => (
					<CardOrientationWrapper
						cardId={card.cardId}
						key={index}
						isFirstRowFull={isFirstRowFull}
						cardWidthOnFullRow={cardWidthOnFullRow}
						itemDimensions={cardDimensions}
						setWidth={index === 0 ? setCurrentCardWidth : undefined}
						scrollToCard={index === scrollToIndex}
						clearScrollToCard={resetScrollIndex}
						testId={`link-cards-content-cards-${orientation}-card-${index}`}
						index={index}
						requestScrollIndex={requestScrollIndex}
						extensionLocalId={extensionLocalId}
					>
						{renderCard(card)}
					</CardOrientationWrapper>
				))}
				<WidthObserver setWidth={throttledSetWidth} offscreen />
			</Box>
			{cards.length > 0 && CardsContentFooter && (
				<CardsContentFooter
					isStacked={isStacked}
					showAIDisclaimer={showAIDisclaimer}
					allCardsShown={allCardsShown}
					isAtBeginning={isAtBeginning}
					isAtEnd={isAtEnd}
					scrollToPrevious={scrollToPrevious}
					scrollToNext={scrollToNext}
				/>
			)}
		</Stack>
	);
};
